import {BaseElement, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {BarActions, BarShared, OBarActionsInit, OBarSharedInit} from "back/commons/widgets/bars";
import {Action} from "lib/commons/actions";
import {IReg, IUiEnv, REG} from "lib/commons/registry";
import {JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";

/**
 * Entête standard d'une app :
 *
 * [...MainBar...][Zone custom en slot][...MoreBar...]
 * [....................SharedBar....................]
 *
 * La MainBar, MoreBar et la SharedBar partagent le même ationContext.
 * La SharedBar peut être éliminée par la userData "app.sharedBar.hide".
 *
 */
export interface AppHeader<C> extends BaseElement {
	initialize(init?: OAppHeaderInit<C>): this;
}

export interface OAppHeaderInit<C> extends OSkinableInit {
	/** Contexte pour les actions. */
	actionContext: C

	/** Liste des actions pour la MainBar. */
	mainActions?: Action<C>[]

	/** Liste des actions pour la MainBar. */
	moreActions?: Action<C>[]

	/** Noeuds racines sur lesquels écouter les events pour la sharedBar et le menu contextuel. */
	focusListening?: HTMLElement | HTMLElement[]

	/** Init de la MainBar (actionContext et actions inutiles). */
	mainBar?: OBarActionsInit<C>

	/** Init de la MoreBar (actionContext et actions inutiles). */
	moreBar?: OBarActionsInit<C>

	/** Init de la SharedBar. */
	sharedBar?: OSkinableInit

	/** Force l'affichage de la sharedBar quelque-soit la userData "app.sharedBar.hide"*/
	forceSharedBar?: boolean;
}


export class AppHeader<C> extends BaseElement {
	reg: IReg<IUiEnv>;

	/** ActionContext pour les mainActions, moreActions, startActions et endActions de la sharedBar. */
	actionContext: C;

	protected _headband: MsgLabel;

	protected _mainActions: BarActions<C> | undefined;

	protected _barActions: BarActions<C> | undefined;

	protected _initialize(init: OAppHeaderInit<C>) {
		this.reg = this.findReg(init);
		this.actionContext = init.actionContext;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		const row1 = sr.appendChild(<div>
			<slot/>
		</div>);
		if (init.forceSharedBar || !this.reg.getUserData('app.sharedBar.hide', true)) {
			//La sharedBar ne peut pas être dans le shadow.
			sr.appendChild(<slot name="bar"/>);
			const initBar = BASIS.newInit(init.sharedBar as OBarSharedInit<C>, this.reg);
			initBar.actionContext = init.actionContext;
			initBar.focusListening = init.focusListening;
			this.appendChild(<BarShared î={initBar} slot="bar"/>);
		}
		if (init.mainActions && init.mainActions.length > 0) {
			const initMain = BASIS.newInit(init.mainBar, this.reg) as OBarActionsInit<C>;
			initMain.actionContext = init.actionContext;
			initMain.actions = init.mainActions;
			this._mainActions = row1.insertBefore(new BarActions().initialize(initMain), row1.firstChild) as BarActions<C>;
		}
		if (init.moreActions && init.moreActions.length > 0) {
			const initMore = BASIS.newInit(init.moreBar, this.reg) as OBarActionsInit<C>;
			initMore.actionContext = init.actionContext;
			initMore.actions = init.moreActions;
			this._barActions = row1.appendChild(new BarActions().initialize(initMore)) as BarActions<C>;
		}
	}

	refresh() {
		super.refresh();
		if (this._mainActions) this._mainActions.refresh();
		if (this._barActions) this._barActions.refresh();
	}

	/** Affiche une erreur ou une info dans un bandeau supplémentaire. */
	setHeadband(msg: string, level?: 'info' | 'error', icon?: string) {
		if (msg) {
			if (!this._headband) this._headband = <MsgLabel class="headband"/> as MsgLabel;
			if (!this._headband.isConnected) this.shadowRoot.insertBefore(this._headband, this.shadowRoot.firstChild);
			this._headband.setCustomMsg(msg, level, icon);
		} else {
			if (this._headband && this._headband.isConnected) this._headband.remove();
		}
	}
}


REG.reg.registerSkin('c-appheader', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: min-content;
		min-width: 0;
		flex-direction: column;
		justify-content: center;
	}

	div {
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	slot {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
	}

	c-button-actions {
		padding: .2em;
		margin: .2em;
		background: none;
	}
`);

customElements.define('c-appheader', AppHeader);
